<script lang="ts">
	// UC-10. The options an item can offer at the till (Size, Extras), each
	// with its choices and what each choice adds or takes off. Beside every
	// option is how the till will show it, live, so an owner setting "at
	// least 1, up to 3" sees what a cashier will see.
	//
	// A choice cannot be edited, because the API has no way to; the card
	// says so, and removing and adding it again is the way to change one.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { api } from '../api/client';
	import { fill, isArabic, message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { loadMenu, ownerMenu } from '../owner/data.svelte';
	import { ruleWords as choiceRule } from '../owner/draft.svelte';
	import { flash } from '../owner/notices.svelte';
	import { PriceInput } from '../owner/price.svelte';
	import type { components } from '../api/types';

	type Group = components['schemas']['GroupBody'];
	type Kind = 'fixed' | 'perKg';

	interface Props {
		language: Language;
		minorDigits: number;
	}

	let { language, minorDigits }: Props = $props();

	// The API's bound on how many choices an option can ask for.
	const MAX_SELECT = 20;

	const menu = $derived(ownerMenu());
	const groups = $derived(menu.value.groups ?? []);
	const items = $derived(menu.value.items ?? []);

	let failure = $state('');
	let busy = $state(false);

	// The option being edited, or a new one ('new'). Its kind is picked only
	// when it is added (UC-10.R10); an edit shows it and never sends it.
	let editing = $state<{
		id: string;
		name: string;
		min: string;
		max: string;
		kind: Kind;
	} | null>(null);
	// The option a choice is being added to.
	// Its price change is read as written; the sign button, or a typed minus,
	// makes it take money off. Left empty it costs nothing extra.
	let adding = $state<{ groupId: string; name: string; delta: PriceInput; sign: 1 | -1 } | null>(
		null,
	);

	function startAdding(groupId: string) {
		adding = {
			groupId,
			name: '',
			delta: new PriceInput(() => minorDigits, { negative: true, optional: true }),
			sign: 1,
		};
	}
	// What the till preview has picked, per option. Only a preview.
	let previews = $state<Record<string, string | string[]>>({});

	// The same words the Publish sheet reads a changed rule in.
	function ruleWords(min: number, max: number): string {
		return choiceRule(language, min, max);
	}

	function tillRule(min: number, max: number): string | undefined {
		if (min < 1) return undefined;
		return min === 1 && max === 1
			? message(language, 'editorChooseOne')
			: `${message(language, 'editorChooseAtLeast')} ${min}`;
	}

	function signed(minor: number): string {
		if (minor === 0) return formatMinor(0, minorDigits);
		return minor > 0 ? `+${formatMinor(minor, minorDigits)}` : formatMinor(minor, minorDigits);
	}

	// A per-kg group's choice is priced per kilogram: "+50.00 / كجم".
	function choicePrice(group: Group, minor: number): string {
		return group.kind === 'perKg'
			? fill(message(language, 'perKilo'), signed(minor))
			: signed(minor);
	}

	function kindWords(kind: Kind | undefined): string {
		return message(language, kind === 'perKg' ? 'menuGroupPerKg' : 'menuGroupFixed');
	}

	function usedBy(group: Group): string {
		const names = items.filter((i) => (i.groups ?? []).includes(group.id)).map((i) => i.name);
		return names.length > 0
			? names.join(isArabic(language) ? '، ' : ', ')
			: message(language, 'menuNoItemsYet');
	}

	const range = $derived.by(() => {
		if (!editing) return { min: 0, max: 0, ok: false };
		const min = Number(editing.min || '0');
		const max = Number(editing.max || '0');
		return { min, max, ok: max >= 1 && max >= min && max <= MAX_SELECT && min <= MAX_SELECT };
	});

	async function refresh() {
		await loadMenu(message(language, 'loadFailed'));
	}

	async function run(
		task: () => Promise<{ error?: { message?: string; details?: Record<string, unknown> } }>,
	): Promise<boolean> {
		if (busy) return false;
		busy = true;
		failure = '';
		try {
			const { error } = await task();
			if (error !== undefined) {
				// A server just started holds new per-kg groups for a few
				// minutes (WeighedCreateHold), refused at kind.
				failure =
					error.details?.path === 'kind'
						? message(language, 'menuPerKgSoon')
						: (error.message ?? message(language, 'loadFailed'));
				return false;
			}
			await refresh();
			return true;
		} catch {
			failure = message(language, 'loadFailed');
			return false;
		} finally {
			busy = false;
		}
	}

	async function saveGroup() {
		const form = editing;
		if (!form || !form.name.trim() || !range.ok) return;
		const body = { name: form.name.trim(), minSelect: range.min, maxSelect: range.max };
		const ok = await run(() =>
			form.id === 'new'
				? api.POST('/api/owner/menu/groups', { body: { ...body, kind: form.kind } })
				: api.PATCH('/api/owner/menu/groups/{groupId}', {
						params: { path: { groupId: form.id } },
						body,
					}),
		);
		if (ok) {
			editing = null;
			flash(message(language, 'saved'));
		}
	}

	async function removeGroup(groupId: string) {
		const ok = await run(() =>
			api.POST('/api/owner/menu/groups/{groupId}/remove', { params: { path: { groupId } } }),
		);
		if (ok) editing = null;
	}

	async function addChoice(group: Group) {
		const form = adding;
		if (!form || !form.name.trim()) return;
		form.delta.tidy();
		if (!form.delta.valid) return;
		const typed = form.delta.minor ?? 0;
		const priceDeltaMinor = typed < 0 ? typed : form.sign * typed;
		const ok = await run(() =>
			api.POST('/api/owner/menu/groups/{groupId}/modifiers', {
				params: { path: { groupId: group.id } },
				body: {
					name: form.name.trim(),
					priceDeltaMinor,
					available: true,
					sortOrder: (group.modifiers ?? []).length * 10,
				},
			}),
		);
		if (ok) adding = null;
	}

	async function removeChoice(modifierId: string) {
		await run(() =>
			api.POST('/api/owner/menu/modifiers/{modifierId}/remove', {
				params: { path: { modifierId } },
			}),
		);
	}

	const cards = $derived([
		...(editing?.id === 'new'
			? [
					{
						id: 'new',
						name: '',
						minSelect: 0,
						maxSelect: 1,
						modifiers: [],
						kind: editing.kind,
					} as Group,
				]
			: []),
		...groups,
	]);
</script>

<div class="options" data-testid="menu-options">
	{#if failure}
		<Band tone="flag" testId="options-error">{failure}</Band>
	{/if}
	<div class="head">
		<Button
			testId="option-add"
			icon="plus"
			label={message(language, 'menuNewOption')}
			tone="ink"
			size="sm"
			onPress={() => (editing = { id: 'new', name: '', min: '0', max: '1', kind: 'fixed' })}
		/>
	</div>

	{#each cards as group (group.id)}
		{@const isEditing = editing?.id === group.id}
		{@const min = isEditing ? range.min : group.minSelect}
		{@const max = isEditing ? range.max : group.maxSelect}
		{@const single = min === 1 && max === 1}
		<Card
			title={group.id === 'new'
				? message(language, 'menuNewOption')
				: `${group.name} · ${ruleWords(group.minSelect, group.maxSelect)}`}
			testId="option-{group.id}"
		>
			<div class="body">
				<div class="left">
					{#if isEditing && editing}
						<div class="kind">
							<span class="label">{message(language, 'menuGroupKind')}</span>
							{#if group.id === 'new'}
								<Segmented
									label={message(language, 'menuGroupKind')}
									testId="option-kind"
									options={[
										{
											value: 'fixed',
											label: message(language, 'menuGroupFixed'),
											testId: 'option-kind-fixed',
										},
										{
											value: 'perKg',
											label: message(language, 'menuGroupPerKg'),
											testId: 'option-kind-perKg',
										},
									]}
									value={editing.kind}
									onChange={(picked) =>
										editing && (editing.kind = picked === 'perKg' ? 'perKg' : 'fixed')}
								/>
							{:else}
								<span data-testid="option-kind">{kindWords(editing.kind)}</span>
							{/if}
						</div>
						<div class="limits">
							<Field
								testId="option-name"
								label={message(language, 'menuOption')}
								value={editing.name}
								maxLength={120}
								onInput={(v) => editing && (editing.name = v)}
							/>
							<Field
								testId="option-min"
								label={message(language, 'menuMin')}
								value={editing.min}
								inputMode="numeric"
								mono
								onInput={(v) => editing && (editing.min = v.replace(/\D/g, '').slice(0, 2))}
							/>
							<Field
								testId="option-max"
								label={message(language, 'menuMax')}
								value={editing.max}
								error={range.ok ? undefined : message(language, 'menuRangeError')}
								inputMode="numeric"
								mono
								onInput={(v) => editing && (editing.max = v.replace(/\D/g, '').slice(0, 2))}
							/>
						</div>
						<p class="rule">
							{message(language, 'menuOnTillColon')}
							<strong>{ruleWords(range.min, range.max)}</strong>
						</p>
						<div class="row">
							<Button
								testId="option-save"
								label={message(language, 'save')}
								size="sm"
								disabled={!editing.name.trim() || !range.ok || busy}
								onPress={() => void saveGroup()}
							/>
							<Button
								testId="option-cancel"
								label={message(language, 'cancel')}
								tone="text-quiet"
								size="sm"
								onPress={() => (editing = null)}
							/>
							<span class="grow"></span>
							{#if group.id !== 'new'}
								<Button
									testId="option-remove"
									label={message(language, 'menuRemoveOption')}
									tone="text-quiet"
									size="sm"
									disabled={busy}
									onPress={() => void removeGroup(group.id)}
								/>
							{/if}
						</div>
					{/if}

					{#each group.modifiers ?? [] as choice (choice.id)}
						<div class="choice" data-testid="choice-row-{choice.id}">
							<span class="choice-name">{choice.name}</span>
							<span class="n" dir="ltr">{choicePrice(group, choice.priceDeltaMinor)}</span>
							{#if !choice.available}
								<Chip text={message(language, 'menuSoldOut')} tone="flag" size="md" dot />
							{/if}
							<Button
								testId="choice-remove-{choice.id}"
								label={message(language, 'remove')}
								tone="text-quiet"
								size="sm"
								disabled={busy}
								onPress={() => void removeChoice(choice.id)}
							/>
						</div>
					{/each}

					{#if group.id !== 'new'}
						{#if adding && adding.groupId === group.id}
							<div class="adding">
								<div class="grow-field">
									<Field
										testId="choice-name"
										ariaLabel={message(language, 'menuChoiceName')}
										placeholder={message(language, 'menuChoiceName')}
										value={adding.name}
										maxLength={120}
										onInput={(v) => adding && (adding.name = v)}
									/>
								</div>
								<div class="delta">
									<Field
										testId="choice-delta"
										ariaLabel={message(language, 'menuPriceChange')}
										value={adding.delta.text}
										placeholder={formatMinor(0, minorDigits)}
										error={adding.delta.error(language)}
										inputMode="decimal"
										mono
										onInput={adding.delta.set}
										onBlur={adding.delta.tidy}
									/>
								</div>
								<Button
									testId="choice-sign"
									label={adding.sign > 0 ? '+' : '−'}
									ariaLabel={message(language, 'menuAddOrTake')}
									tone="secondary"
									size="sm"
									mono
									onPress={() => adding && (adding.sign = adding.sign > 0 ? -1 : 1)}
								/>
								<Button
									testId="choice-add-save"
									label={message(language, 'menuAdd')}
									size="sm"
									disabled={!adding.name.trim() || busy}
									onPress={() => void addChoice(group)}
								/>
								<Button
									testId="choice-add-cancel"
									label={message(language, 'cancel')}
									tone="text-quiet"
									size="sm"
									onPress={() => (adding = null)}
								/>
							</div>
						{/if}
						<div class="row">
							{#if !(adding && adding.groupId === group.id)}
								<Button
									testId="choice-add-{group.id}"
									icon="plus"
									label={message(language, 'menuChoice')}
									tone="secondary"
									size="sm"
									onPress={() => startAdding(group.id)}
								/>
							{/if}
							{#if !isEditing}
								<Button
									testId="option-edit-{group.id}"
									label={message(language, 'menuEditOption')}
									tone="text"
									size="sm"
									onPress={() =>
										(editing = {
											id: group.id,
											name: group.name,
											min: String(group.minSelect),
											max: String(group.maxSelect),
											kind: group.kind === 'perKg' ? 'perKg' : 'fixed',
										})}
								/>
							{/if}
						</div>
						<p class="note">{message(language, 'menuChoiceNote')} {usedBy(group)}</p>
					{/if}
				</div>
				<div class="preview">
					<span class="eyebrow">{message(language, 'menuOnTill')}</span>
					<Choice
						title={isEditing && editing ? editing.name : group.name}
						rule={tillRule(min, max)}
						mode={single ? 'single' : 'multi'}
						{min}
						{max}
						value={previews[group.id] ?? (single ? '' : [])}
						onChange={(picked) => (previews[group.id] = picked)}
						soldOutLabel={message(language, 'menuSoldOut')}
						options={(group.modifiers ?? []).map((m) => ({
							value: m.id,
							label: m.name,
							price: m.priceDeltaMinor === 0 ? undefined : choicePrice(group, m.priceDeltaMinor),
							soldOut: !m.available,
							testId: `preview-${group.id}-${m.id}`,
						}))}
					/>
				</div>
			</div>
		</Card>
	{/each}
</div>

<style>
	.options {
		display: flex;
		flex-direction: column;
		gap: var(--grid-gap);
	}

	.head {
		display: flex;
		justify-content: flex-end;
	}

	.body {
		padding: var(--card-pad);
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
		gap: var(--space-9);
	}

	.left {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.limits {
		display: grid;
		grid-template-columns: minmax(0, 2fr) repeat(2, minmax(0, 1fr));
		gap: var(--space-4);
	}

	.kind {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		align-items: flex-start;
		font-size: var(--text-sm);
	}

	.kind .label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.rule {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.rule strong {
		font-weight: 600;
		color: var(--text-strong);
	}

	.row {
		display: flex;
		gap: var(--space-3);
		flex-wrap: wrap;
		align-items: center;
	}

	.grow {
		flex: 1;
	}

	.choice {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-3) 0;
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.choice-name {
		flex: 1;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.n {
		font-family: var(--font-mono);
		font-size: var(--text-sm);
	}

	.adding {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
		align-items: center;
	}

	.grow-field {
		flex: 1 1 140px;
		min-inline-size: 0;
	}

	.delta {
		inline-size: 110px;
	}

	.note {
		margin: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.preview {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		padding: var(--space-7);
		background: var(--surface-page);
		border-radius: var(--radius-card);
	}

	.eyebrow {
		font-family: var(--font-mono);
		font-size: var(--text-micro);
		letter-spacing: var(--tracking-label);
		color: var(--text-muted);
	}
</style>
